<template>
  <div class="project-consumed-items">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <DataTable
      v-else
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getConsumedItems"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getConsumedItemsCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="item.number"
        :header="$t('item_number')"
        sortable
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center;width: 100px'"
        filterField="itemNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.item && slotProps.data.item.number !== null">
            {{ slotProps.data.item.number.toString().padStart(5, '0') }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="item.description"
        :header="$t('item_description')"
        sortable
        filterMatchMode="contains"
        filterField="itemDescription"
      >
        <template #body="slotProps">
          <span
            v-if="slotProps.data && slotProps.data.item && slotProps.data.item.description !== null"
          >
            {{ slotProps.data.item.description }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="totalQuantityConsumed"
        :header="$t('quantity_consumed')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center;width: 120px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.totalQuantityConsumed !== null">
            <strong>{{ slotProps.data.totalQuantityConsumed }}</strong>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            placeholder=">="
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-consumed-items ("Verbrauchte Materialien", see feature page materialwirtschaft):
 * the read-only consumption list of the project. It loads through the block (`projectNumber`); the
 * server forces the list to the project, so the table state has no project filter.
 *
 * No `BlockFieldset` root: the block is viewer-only and has no write control, and a disabled
 * fieldset would also disable the filters and the paging that viewers need to read.
 */
export default {
  name: 'ProjectConsumedItems',
  components: {
    DataTable,
    InputText,
    Column,
  },
  props: {
    /** The page project; the block data (the consumption list) is loaded by the block itself. */
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      consumedItemsTableStateConfig: {
        name: `projectItemsConsumed-${this.project.number}`,
        filters: {
          itemNumber: { value: null },
          itemDescription: { value: null },
          totalQuantityConsumed: { value: null },
        },
        customFilters: {},
        sorting: { sortField: 'totalQuantityConsumed', sortOrder: -1 },
      },
    };
  },
  computed: {
    ...mapGetters(['getConsumedItems', 'getConsumedItemsCount', 'isLoading', 'getTableState']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    tableStateStore() {
      return this.getTableState(this.consumedItemsTableStateConfig.name);
    },
  },
  methods: {
    ...mapActions(['fetchConsumedItemsByProjectPaginated', 'initTableState']),

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchConsumedItemsByProjectPaginated({
        projectNumber: this.project.number,
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },
  },
  async created() {
    await this.initTableState(this.consumedItemsTableStateConfig);
  },
  async mounted() {
    if (!this.isNewProject) await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}
::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 30vh;
}
</style>
